import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate, useParams } from "react-router-dom";
import toastr from "react-redux-toastr";
import debounce from 'lodash/debounce';
import ScheduledPayoutComponent from "./component";
import * as XLSX from 'xlsx';

const ScheduledPayoutContainer = (props) => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [apiResponse, setApiResponse] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [chargeSummery, setChargeSummery] = useState(null);
  const [currentPayoutId, setCurrentPayoutId] = useState(null);
  const [payoutReportData, setPayoutReportData] = useState([]);

  useEffect(() => {
    fetchScheduledPayoutData();
  }, [date]);

  useEffect(() => {
    const amount = bills
      .filter(bill => selectedBills.includes(bill.payoutId))
      .reduce((sum, bill) => sum + bill.netPayable, 0);
    setSelectedAmount(amount);
  }, [selectedBills, bills]);

  const fetchScheduledPayoutData = debounce(() => {
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v3/acc/payouts/pending?date=${date}`,
      'GET',
      undefined,
      (response) => {
        if (response) {
          setBills(response);
          calculateTotal(response);
          if (response.length > 0) {
            setCurrentPayoutId(response[0].payoutId);
          }
        } else {
          setBills([]);
          setTotalAmount(0);
        }
        setLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error(err.message || "Error", "Failed to load scheduled payouts");
        setLoading(false);
      }
    );
  }, 300);

  const fetchChargeBillSummery = (payoutId) => {
    if (!payoutId) {
      toastr.warning("Warning", "No payout ID available");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v3/acc/payout/${payoutId}/deduction`,
      'GET',
      undefined,
      (response) => {
        setChargeSummery(response);
        setLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error(err.message || "Error", "Failed to load charge summary");
        setLoading(false);
      }
    );
  };

  const calculateTotal = (bills) => {
    const total = bills.reduce((sum, bill) => sum + bill.netPayable, 0);
    setTotalAmount(total);
  };

  const handleCheckboxChange = (billId) => {
    setSelectedBills(prev => {
      if (prev.includes(billId)) {
        return prev.filter(id => id !== billId);
      } else {
        return [...prev, billId];
      }
    });
  };

  const handleSelectAll = (e, filteredBills) => {
    if (e.target.checked) {
      setSelectedBills(filteredBills.map(bill => bill.payoutId));
    } else {
      setSelectedBills([]);
    }
  };

  const handleDownloadExcel = (reportData) => {
    try {
      if (!reportData || reportData.length === 0) {
        toastr.warning("Warning", "No data available for export");
        return;
      }

      const dataToExport = reportData.cohortScheduleResponseDto.map(item => ({
        "Partner ID": item.franchiseeId || "N/A",
        "Partner Name": item.payee || "N/A",
        "Status": item.status || "Generated",
        "Deductions": item.deductions 
          ? JSON.stringify(item.deductions)
          : "0",
        "Payout": item.payout || 0,
        "Transfer Text": item.transferText || "N/A",
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payouts Report");

      XLSX.writeFile(wb, `payout-report-${new Date().toISOString().split("T")[0]}.xlsx`);

      toastr.success("Success", "Excel file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    }
  };

  const handleMarkSettled = () => {
    if (selectedBills.length === 0) {
      toastr.warning("Warning", "Please select at least one bill to mark as settled");
      return;
    }

    props.getDataFromAPI(
      '/partner/api/v3/acc/payouts/mark-schedule/report',
      "PATCH",
      { payoutIds: selectedBills },
      (response) => {
        setApiResponse(response);
        setPayoutReportData(response || []);
        setShowSuccessDialog(true);
        setShowResponseDialog(true);
        
        handleDownloadExcel(response, `payout-report-${date}`);
        
        setSelectedBills([]);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error(err.message || "Failed to mark bills as settled");
      }
    );
  };

  const handleSuccessClose = (date) => {
    setShowSuccessDialog(false);
    navigate(`/home/configuration/payoutpartners/schedule/download-report`, {
      state: { payoutReportData }
    });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <ScheduledPayoutComponent
      loading={loading}
      date={date}
      bills={bills}
      selectedBills={selectedBills}
      totalAmount={selectedBills.length === bills.length ? totalAmount : selectedAmount}
      onCheckboxChange={handleCheckboxChange}
      onSelectAll={handleSelectAll}
      onMarkSettled={handleMarkSettled}
      onGoBack={handleGoBack}
      apiResponse={apiResponse}
      showSuccessDialog={showSuccessDialog}
      onSuccessClose={handleSuccessClose}
      showResponseDialog={showResponseDialog}
      setShowResponseDialog={setShowResponseDialog}
      fetchChargeBillSummery={fetchChargeBillSummery}
      chargeSummery={chargeSummery}
      currentPayoutId={currentPayoutId}
      setCurrentPayoutId={setCurrentPayoutId}
    />
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {
  getDataFromAPI,
})(ScheduledPayoutContainer);